import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';
import SignatureCanvas from '@/components/SignatureCanvas';
import { generateAndSavePagePDF } from '@/utils/pdfGenerator';
import html2pdf from 'html2pdf.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const STAMP_URL = '/images/TCA_Company_Stamp.webp'; // TC Auditors' Official Company Stamp (as on the B-BBEE site)
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const PageFour = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state || {};
  
  // Get formId from previous page
  const formId = clientData.formId;
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Ref for signature canvas to restore signature before PDF generation
  const signatureCanvasRef = useRef(null);
  
  // Initialize with previous data if coming back from Page 5
  const [signature, setSignature] = useState(clientData.signaturePage4 || null);
  const [signedAt, setSignedAt] = useState(clientData.signedAtPage4 || '');
  const [showLocationWarning, setShowLocationWarning] = useState(false);
  const [locationError, setLocationError] = useState('');
  
  // Dynamic agreement fees state
  const [agreementFees, setAgreementFees] = useState(null);
  const [isPricingLoaded, setIsPricingLoaded] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch agreement fees on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/pricing`);
        const data = await response.json();
        setAgreementFees(data.agreementFees || {
          minInitiationFee: 750,
          minSubmissionFee: 750,
          dailyLatePenalty: 250,
          maxLatePenaltyDays: 5,
          terminationFee: 750
        });
      } catch (error) {
        console.error('Error fetching pricing:', error);
        // Use defaults on error
        setAgreementFees({
          minInitiationFee: 750,
          minSubmissionFee: 750,
          dailyLatePenalty: 250,
          maxLatePenaltyDays: 5,
          terminationFee: 750
        });
      } finally {
        setIsPricingLoaded(true);
      }
    };
    
    fetchPricing();
  }, [BACKEND_URL]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Format currency with thousand separators and 2 decimal places
  // Example: 1250 -> "R1,250.00"
  const formatCurrencyFull = (amount) => {
    if (amount === undefined || amount === null) return 'R0.00';
    // Use en-US for comma thousand separators, then add R prefix
    const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `R${formatted}`;
  };

  // Convert number to words (for legal documents)
  // Supports 0-999,999 for currency amounts
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
                  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 
                  'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const convertHundreds = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertHundreds(n % 100) : '');
    };
    
    if (num < 1000) return convertHundreds(num);
    if (num < 1000000) {
      const thousands = Math.floor(num / 1000);
      const remainder = num % 1000;
      return convertHundreds(thousands) + ' Thousand' + (remainder ? ' ' + convertHundreds(remainder) : '');
    }
    return num.toString(); // Fallback for very large numbers
  };

  // Convert small numbers (1-20) to lowercase words for inline text
  const numberToWordSmall = (num) => {
    const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 
                   'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen',
                   'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty'];
    return words[num] || num.toString();
  };

  // Calculate max late payment penalty (dynamic: days × daily penalty)
  const calculateMaxLatePenalty = () => {
    if (!agreementFees) return 0;
    return agreementFees.maxLatePenaltyDays * agreementFees.dailyLatePenalty;
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get today's date formatted
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthName = today.toLocaleDateString('en-ZA', { month: 'long' });
  const fullYear = today.getFullYear();

  // Construct full name from client data
  const getFullName = () => {
    const parts = [
      clientData.firstName,
      clientData.secondName,
      clientData.middleName,
      clientData.surname
    ].filter(Boolean);
    return parts.join(' ') || '[Client Full Name]';
  };

  const fullName = getFullName();
  const idNumber = clientData.idNumber || '[ID Number]';

  // Validate location - only allow alphabetic letters and spaces
  const validateLocation = (value) => {
    if (!value || value.trim() === '') {
      return '';
    }
    // Check if the value contains only letters and spaces
    const locationRegex = /^[a-zA-Z\s]+$/;
    if (!locationRegex.test(value)) {
      return 'Location can only contain letters (no numbers or special characters)';
    }
    return '';
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSignedAt(value);
    setLocationError(validateLocation(value));
  };

  const handleSignatureChange = (signatureData) => {
    setSignature(signatureData);
  };

  const handleClearSignature = () => {
    setSignature(null);
  };

  // Check if form is valid (location and signature required)
  const isFormValid = () => {
    return signedAt && signedAt.trim() !== '' && signature && !locationError;
  };

  const handleSubmit = async () => {
    if (!signedAt || signedAt.trim() === '') {
      alert('Please enter the location where you are signing.');
      return;
    }
    // Validate location before proceeding
    const locationValidationError = validateLocation(signedAt);
    if (locationValidationError) {
      alert(locationValidationError);
      return;
    }
    if (!signature) {
      alert('Please provide your signature before proceeding.');
      return;
    }
    
    // Check if location on Page 3 (POPI Consent Agreement) differs from Page 4 (Tax Services Master Engagement Letter)
    const page3Location = clientData.signedAtPage3 || '';
    const page4Location = signedAt.trim();
    
    if (page3Location && page4Location && page3Location.toLowerCase() !== page4Location.toLowerCase()) {
      setShowLocationWarning(true);
      return;
    }
    
    // Restore signature to canvas before PDF generation (in case it was cleared by zoom/resize)
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.restoreSignature();
    }
    
    // Generate PDF of current page before navigating
    setIsGeneratingPDF(true);
    
    // Create full-screen overlay to hide PDF generation process from user
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generation-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.97);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0;">Saving your progress...</p>
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">Please wait while we generate your PDF</p>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
    document.body.appendChild(overlay);
    
    const element = document.querySelector('#page-pdf-content');
    if (!element) {
      overlay.remove();
      setIsGeneratingPDF(false);
      alert('Error: Could not find page content.');
      return;
    }
    
    // Store original values for restoration
    const originalStyles = new Map();
    const hiddenElements = [];
    const originalClassNames = new Map();
    
    const hideElement = (el) => {
      if (el) {
        originalStyles.set(el, el.style.cssText);
        el.style.display = 'none';
        hiddenElements.push(el);
      }
    };
    
    const modifyStyle = (el, newStyle) => {
      if (el) {
        if (!originalStyles.has(el)) {
          originalStyles.set(el, el.style.cssText);
        }
        Object.assign(el.style, newStyle);
      }
    };
    
    const modifyClassName = (el, newClassName) => {
      if (el) {
        if (!originalClassNames.has(el)) {
          originalClassNames.set(el, el.className);
        }
        el.className = newClassName;
      }
    };
    
    try {
      // === PDF MODIFICATIONS FOR PAGE 4 ===
      // Keep the PDF's original top spacing (the page itself has only 2px above the logo; was py-6 sm:py-8)
      modifyStyle(element, { paddingTop: window.matchMedia('(min-width: 640px)').matches ? '2rem' : '1.5rem' });
      
      // 1. Reduce top margin/padding
      modifyStyle(element, { paddingTop: '2px' });
      element.querySelectorAll('.py-6, .sm\\:py-8, .py-4').forEach(el => {
        modifyStyle(el, { paddingTop: '2px', paddingBottom: '2px' });
      });
      
      // 2. Reduce heading margins
      element.querySelectorAll('h1, h2, h3').forEach(el => {
        modifyStyle(el, { marginTop: '4px', marginBottom: '4px' });
      });
      
      // 3. Reduce paragraph margins and line heights
      element.querySelectorAll('p').forEach(el => {
        modifyStyle(el, { marginBottom: '3px', lineHeight: '1.35' });
      });
      
      // 4. Reduce section spacing
      element.querySelectorAll('.mt-6, .mt-8, .mb-6, .mb-8').forEach(el => {
        const currentClass = el.className;
        modifyClassName(el, currentClass.replace(/mt-[68]/g, 'mt-2').replace(/mb-[68]/g, 'mb-2'));
      });
      
      // 5. Reduce card padding
      element.querySelectorAll('.p-4.sm\\:p-8').forEach(el => {
        modifyStyle(el, { padding: '8px 12px' });
      });
      
      // 6. Reduce space-y gaps
      element.querySelectorAll('.space-y-4, .space-y-6').forEach(el => {
        modifyStyle(el, { gap: '4px' });
        // Apply to direct children
        Array.from(el.children).forEach(child => {
          modifyStyle(child, { marginTop: '4px', marginBottom: '4px' });
        });
      });
      
      // 7. Reduce gap between top two headings (TAX SERVICES AGREEMENT and TAX SERVICES ENGAGEMENT AGREEMENT)
      element.querySelectorAll('h2.text-xl.font-bold.text-center').forEach(el => {
        modifyStyle(el, { marginBottom: '0px', paddingBottom: '0px' });
      });
      element.querySelectorAll('p.text-center.font-semibold').forEach(el => {
        modifyStyle(el, { marginTop: '0px', paddingTop: '0px', marginBottom: '4px' });
      });
      
      // 7a. Add spacing after "ENTERED INTO BY AND BETWEEN" to prevent overlap with first blue field
      element.querySelectorAll('p').forEach(p => {
        if (p.textContent?.includes('ENTERED INTO BY AND BETWEEN')) {
          modifyStyle(p, { marginBottom: '8px', paddingBottom: '4px' });
        }
      });
      
      // 7b. Fix blue fields at the top - background shifted down to cover text
      element.querySelectorAll('.bg-blue-50, [class*="bg-blue"]').forEach(field => {
        if (field.tagName === 'INPUT') return;
        modifyStyle(field, { 
          display: 'inline-block', position: 'relative', top: '-2px',
          padding: '0px 6px 5px 6px', margin: '0 2px', borderRadius: '3px',
          lineHeight: '1.4', verticalAlign: 'baseline', boxSizing: 'border-box',
          backgroundColor: '#dbeafe'
        });
      });
      
      // Replace input fields with spans for PDF - background shifted down
      element.querySelectorAll('input.bg-blue-50, input[class*="bg-blue"]').forEach(input => {
        const inputValue = input.value || input.placeholder || '';
        const replacementSpan = document.createElement('span');
        replacementSpan.textContent = inputValue;
        replacementSpan.style.cssText = `
          display: inline-block; position: relative; top: -2px;
          padding: 0px 6px 5px 6px; background-color: #dbeafe; color: #1e3a8a;
          font-weight: bold; border-radius: 3px; min-width: 60px;
          line-height: 1.4; vertical-align: baseline;
        `;
        replacementSpan.id = 'temp-input-replacement-' + Math.random().toString(36).substr(2, 9);
        input.style.display = 'none';
        input.parentNode.insertBefore(replacementSpan, input.nextSibling);
      });
      
      // 8. Scale signature canvas proportionately by 64% (reduce by 40% total) to fit content
      element.querySelectorAll('.border-2.border-dashed').forEach(el => {
        modifyStyle(el, { 
          transform: 'scale(0.64)',
          transformOrigin: 'top left',
          marginBottom: '-60px'  // Compensate for scale reduction
        });
      });
      
      // 8b. Add gap after "Accepted by client by virtue of signature" and "Accepted by Company..."
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent?.includes('Accepted by client by virtue of signature')) {
          modifyStyle(h3, { marginBottom: '5px', paddingBottom: '1px' });
        }
        if (h3.textContent?.includes('Accepted by Company by virtue of Director signature')) {
          modifyStyle(h3, { marginBottom: '5px', paddingBottom: '1px', marginTop: '0px', paddingTop: '0px' });
        }
      });
      
      // 8c. Fix blue fields - text at baseline, gradient extended down to cover text
      element.querySelectorAll('.font-bold.text-tca-blue.bg-blue-50').forEach(field => {
        modifyStyle(field, { 
          display: 'inline-block', position: 'relative', top: '-1px',
          padding: '0px 6px 4px 6px', margin: '0 2px', borderRadius: '3px',
          lineHeight: '1.4', verticalAlign: 'baseline', boxSizing: 'border-box',
          backgroundColor: '#dbeafe'
        });
      });
      
      // 8d. Add gap between client's "Signed at" and "Accepted by Company" heading
      element.querySelectorAll('.border-t.pt-6').forEach(el => {
        modifyStyle(el, { paddingTop: '25px', marginTop: '20px', borderTopWidth: '1px' });
      });
      
      // Also add margin-top to the "Accepted by Company" heading directly
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent?.includes('Accepted by Company by virtue of Director signature')) {
          modifyStyle(h3, { marginTop: '20px', paddingTop: '10px' });
        }
      });
      
      // Add margin-bottom to the client's "Signed at" container
      element.querySelectorAll('.bg-gray-50.p-4.rounded-lg').forEach((el, index) => {
        if (index === 0) {  // First one is client's "Signed at"
          modifyStyle(el, { marginBottom: '25px' });
        }
      });
      
      // 8e. Add spacing between "Client signature:" and signature canvas
      // Target the parent div of the signature section and add margin to the label
      element.querySelectorAll('p.font-semibold.mb-3').forEach(p => {
        if (p.textContent?.includes('Client signature:')) {
          modifyStyle(p, { marginBottom: '8px', paddingBottom: '4px' });
        }
      });
      
      // Also add margin-top to the signature canvas container
      element.querySelectorAll('.border-2.border-dashed').forEach(el => {
        modifyStyle(el, { marginTop: '6px' });
      });
      
      // 8f. Reduce spacing between signature canvas and client's "Signed at Meyerton"
      element.querySelectorAll('br').forEach(br => {
        modifyStyle(br, { display: 'none' });
      });
      element.querySelectorAll('.bg-gray-50.p-4.rounded-lg').forEach(el => {
        modifyStyle(el, { marginTop: '4px', paddingTop: '4px', paddingBottom: '4px', marginBottom: '4px' });
      });
      
      // 8g. Fix duplicate location - ensure input field replacement doesn't cause duplication
      element.querySelectorAll('input.border-b-2.border-tca-blue').forEach(input => {
        if (input.style.display !== 'none') {
          input.style.display = 'none';
        }
      });
      
      // 9. Reduce text sizes slightly
      element.querySelectorAll('.text-sm').forEach(el => {
        modifyStyle(el, { fontSize: '13px' });
      });
      element.querySelectorAll('.text-base').forEach(el => {
        modifyStyle(el, { fontSize: '15px' });
      });
      
      // 10. Reduce br spacing
      element.querySelectorAll('br').forEach(el => {
        modifyStyle(el, { lineHeight: '0.5' });
      });
      
      // 11. Reduce list item spacing
      element.querySelectorAll('ul').forEach(ul => {
        modifyStyle(ul, { marginTop: '4px', marginBottom: '4px' });
      });
      element.querySelectorAll('li').forEach(li => {
        modifyStyle(li, { marginBottom: '2px', lineHeight: '1.4' });
      });
      
      // 12. Remove page break before 2.2 - let it flow naturally after 2.1
      // No page break before 2.2
      
      // 12b. PAGE BREAK BEFORE SECTION 2.3
      element.querySelectorAll('p').forEach(p => {
        const spanChild = p.querySelector('span.font-semibold');
        if (spanChild && spanChild.textContent.trim() === '2.3') {
          modifyStyle(p, { 
            pageBreakBefore: 'always', 
            breakBefore: 'page',
            marginTop: '0px',
            paddingTop: '20px'
          });
        }
      });
      
      // 13. NO PAGE BREAK BEFORE SECTION 3 - removed to let content flow naturally
      // Page break moved to after section 5 (before section 6)
      
      // 14. PAGE BREAK BEFORE SECTION 6 (DEBT COLLECTION, CREDIT RATING AND TPN)
      element.querySelectorAll('h3').forEach(h3 => {
        if (h3.textContent.trim() === '6. DEBT COLLECTION, CREDIT RATING AND TPN') {
          modifyStyle(h3, { 
            pageBreakBefore: 'always', 
            breakBefore: 'page',
            marginTop: '0px',
            paddingTop: '20px'
          });
        }
      });
      
      // 15. Reduce spacing from the last section
      element.querySelectorAll('.border-t.pt-6').forEach(el => {
        modifyStyle(el, { paddingTop: '4px', paddingBottom: '0px', marginBottom: '0px' });
      });
      
      element.querySelectorAll('.bg-gray-50.p-4.rounded-lg').forEach((el, index, arr) => {
        if (index === arr.length - 1) {
          modifyStyle(el, { padding: '4px 8px', marginBottom: '0px', marginTop: '4px' });
        }
      });
      
      modifyStyle(element, { paddingBottom: '0px', marginBottom: '0px' });
      
      element.querySelectorAll('.p-4.sm\\:p-8, .sm\\:p-8').forEach(el => {
        modifyStyle(el, { paddingBottom: '2px' });
      });
      
      // Hide elements marked for PDF exclusion
      element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
      
      // SHOW elements that are PDF-only (hidden on website, visible in PDF)
      element.querySelectorAll('[data-pdf-only="true"]').forEach(el => {
        el.style.display = 'flex';
      });
      
      // Add PDF footer with page indicator style (page name, page number, TCA logo)
      const footer = document.createElement('div');
      footer.id = 'temp-pdf-footer';
      footer.style.cssText = `
        width: 100%;
        padding: 16px 0 8px 0;
        margin-top: 20px;
        border-top: 1px solid #e5e7eb;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
      `;
      footer.innerHTML = `
        <div style="font-size: 14px; color: #374151; font-weight: 500; margin-bottom: 8px;">
          Tax Services Master Engagement Letter
        </div>
        <div style="font-size: 13px; color: #6b7280;">
          Document <strong style="color: #111827;">04</strong> of <strong style="color: #111827;">07</strong>
        </div>
        <img src="/images/TCA_Short_Logo.png" alt="TCA Logo" style="position: absolute; right: -6px; top: 50%; transform: translateY(-50%); height: 48px; width: auto; opacity: 0.8;" />
      `;
      element.appendChild(footer);
      
      // Generate PDF
      const pdfOptions = {
        margin: [4, 6, 4, 6],
        filename: `Page04_${formId || 'form'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2, useCORS: true, logging: false, letterRendering: true,
          scrollX: 0, scrollY: 0, windowWidth: element.scrollWidth
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };
      
      const pdfBlob = await html2pdf().set(pdfOptions).from(element).outputPdf('blob');
      
      // === RESTORE ORIGINAL STATE ===
      const tempFooter = document.getElementById('temp-pdf-footer');
      if (tempFooter) tempFooter.remove();
      element.querySelectorAll('[id^="temp-input-replacement-"]').forEach(span => span.remove());
      element.querySelectorAll('input').forEach(input => { 
        if (input.style.display === 'none') input.style.display = ''; 
      });
      originalClassNames.forEach((origClassName, el) => { el.className = origClassName; });
      originalStyles.forEach((origStyle, el) => { el.style.cssText = origStyle; });
      
      // Hide PDF-only elements again
      element.querySelectorAll('[data-pdf-only="true"]').forEach(el => {
        el.style.display = 'none';
      });
      
      // Save PDF to backend with retry logic and verification
      const base64data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(pdfBlob);
      });
      
      const MAX_RETRIES = 3;
      let saveSuccessful = false;
      
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          console.log(`[Page4] Save attempt ${attempt}/${MAX_RETRIES}...`);
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/save-page`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              form_id: formId,
              page_number: 4,
              pdf_data: base64data,
              page_title: 'Tax Services Master Engagement Letter',
              client_data: clientData
            })
          });
          
          if (!response.ok) {
            throw new Error(`Server returned ${response.status}`);
          }
          
          // Verify the PDF was saved
          const verifyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/debug/${formId}`);
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            const page4Saved = verifyData.pages?.some(p => p.page_number === 4);
            if (page4Saved) {
              console.log('[Page4] PDF saved and verified successfully');
              saveSuccessful = true;
              break;
            } else {
              throw new Error('PDF not found after save');
            }
          }
        } catch (err) {
          console.error(`[Page4] Save attempt ${attempt} failed:`, err.message);
          if (attempt < MAX_RETRIES) {
            await new Promise(r => setTimeout(r, 2000));
          }
        }
      }
      
      if (!saveSuccessful) {
        overlay.remove();
        setIsGeneratingPDF(false);
        alert('Failed to save your progress after multiple attempts. Please check your internet connection and try again.');
        return;
      }
      
      // Remove overlay
      overlay.remove();
      setIsGeneratingPDF(false);
      
      // Navigate to Page 5
      navigate('/page-5', { 
        state: { 
          ...clientData,
          signedAtPage4: signedAt,
          signaturePage4: signature
        } 
      });
      
    } catch (error) {
      console.error('[Page4] PDF generation error:', error);
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
      alert('An error occurred while generating your PDF. Please try again.');
    }
  };

  const handleBack = () => {
    // Pass all the client data back to Page 3, including Page 3's signature and location
    navigate('/page-3', { 
      state: {
        ...clientData,
        signedAtPage4: signedAt,
        signaturePage4: signature
      }
    });
  };

  // Show loading state while fetching pricing
  if (!isPricingLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-tca-blue rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading agreement details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content - wrapped for PDF capture */}
      <main id="page-pdf-content" className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8 bg-white">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants" 
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* Tax Services Engagement Letter Document */}
        <Card className="w-full max-w-4xl border shadow-card">
          <CardContent className="p-4 sm:p-8">
            {/* Document Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 underline">TAX SERVICES ENGAGEMENT AGREEMENT</h2>
              <h3 className="text-lg font-semibold text-gray-700 mt-2">MASTER SERVICE LEVEL AGREEMENT</h3>
            </div>

            {/* Parties Section */}
            <div className="space-y-4 mb-6 text-sm sm:text-base text-gray-800">
              <p className="font-semibold">ENTERED INTO BY AND BETWEEN:</p>
              
              <div className="ml-4">
                <p><span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">TC AUDITORS (PTY) LTD</span></p>
                <p className="text-gray-600">(Hereinafter referred to as the "Service Provider")</p>
              </div>
              
              <p className="text-center font-semibold">AND</p>
              
              <div className="ml-4">
                <p><span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded uppercase">{fullName}</span></p>
                <p>ID: <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{idNumber}</span></p>
                <p className="text-gray-600">(Hereinafter referred to as the "Client")</p>
              </div>
              
              <p className="text-gray-600">(Collectively referred to as the "Parties")</p>
            </div>

            {/* Agreement Content */}
            <div className="space-y-4 text-sm sm:text-base text-gray-800 leading-relaxed">
              
              {/* Section 1 */}
              <h3 className="font-bold text-lg underline">1. APPOINTMENT AND SCOPE OF SERVICES</h3>
              
              <p><span className="font-semibold">1.1</span> The Client hereby appoints the Service Provider to render Tax Consulting and Tax Return Submission services.</p>
              
              <p><span className="font-semibold">1.2</span> The specific package selected by the Client, along with detailed line-item pricing, is set out in Annexure A (Pricing Addendum), attached hereto.</p>
              
              <p><span className="font-semibold">1.3</span> Notwithstanding the specifics in Annexure A, this Agreement serves as the Master Service Level Agreement governing the relationship. The Services primarily include:</p>
              <ul className="list-disc list-inside ml-8 space-y-1">
                <li>Ad-hoc Tax Consultations (Telephonic or Virtual); and</li>
                <li>Annual Tax Return Submissions (via SARS eFiling).</li>
              </ul>

              {/* Section 2 */}
              <h3 className="font-bold text-lg underline">2. FEES AND BILLING</h3>
              
              <p><span className="font-semibold">2.1</span> <strong>STEP 1: Initiation Fee (The "Minimum Engagement Fee")</strong></p>
              <p className="ml-4">The Client agrees that a minimum, non-refundable Initiation Fee of {formatCurrencyFull(agreementFees?.minInitiationFee)} is payable immediately upon signature of this Agreement.</p>
              <p className="ml-4"><em>Commencement of Work:</em> The Service Provider shall not commence any services, including the opening of files, accepting instructions, or engaging in tax consultation phone calls, until this Initiation Fee has been received in full.</p>
              <p className="ml-4"><em>Allocation:</em> This fee covers the administrative costs of onboarding the Client and is credited against the initial consultation and file opening.</p>

              <p><span className="font-semibold">2.2</span> <strong>STEP 2: Submission Fee</strong></p>
              <p className="ml-4">Upon completion of the tax calculation and prior to/on the day of the submission of the return to SARS, a fixed Submission Fee of {formatCurrencyFull(agreementFees?.minSubmissionFee)} (per tax year) is payable.</p>
              <p className="ml-4"><em>Payment Due Date:</em> This amount is due immediately upon presentation of the invoice on the day of submission.</p>
              <p className="ml-4"><em>No Deferral:</em> The Client explicitly agrees that payment of the Submission Fee may not be deferred to await the payout of any potential SARS refund. Inability to pay this fee will result in the tax return not being submitted to SARS.</p>

              <p><span className="font-semibold">2.3</span> <strong>STEP 3: Refund Facilitation Fee</strong></p>
              <p className="ml-4">In the event that the services result in a refund due to the Client, a separate "Refund Facilitation Fee" is payable.</p>
              <p className="ml-4"><em>Purpose:</em> This fee covers the additional time utilized by the Service Provider to liaise with SARS, finalize verifications, prepare and submit supporting documents requested by SARS, and track the refund payout.</p>
              <p className="ml-4"><em>Calculation:</em> This fee is calculated as a percentage of the total refund value, as detailed in Annexure A "Pricing Addendum".</p>
              <p className="ml-4"><em>Payment Due Date:</em> This fee is due on the same day that the SARS refund reflects in the Client's bank account.</p>

              <p><span className="font-semibold">2.4</span> <strong>Late Payment Penalty</strong></p>
              <p className="ml-4">The Client agrees that time is of the essence regarding the payment of the Refund Facilitation Fee.</p>
              <p className="ml-4"><em>Penalty:</em> Should the Client fail to pay the Refund Facilitation Fee (and all other outstanding fees) on the same day the refund is received from SARS, a Late Payment Penalty of {formatCurrencyFull(agreementFees?.dailyLatePenalty)} ({numberToWords(agreementFees?.dailyLatePenalty || 0)} Rands) will be charged for every day that the payment is delayed.</p>

              <p><span className="font-semibold">2.5</span> <strong>PAYMENT SECURITY: DEBIT ORDER MANDATE</strong></p>
              <p className="ml-4">The Client shall sign the Debit Order Mandate attached hereto as Annexure B "Debit Order Mandate".</p>
              <p className="ml-4"><em>Security Mechanism:</em> The Client acknowledges that this Debit Order Mandate serves as a security mechanism to guarantee the Service Provider's fees in the event of default.</p>
              <p className="ml-4"><em>Trigger Event:</em> Should the Client fail to pay the Refund Facilitation Fee (and all other outstanding fees) within {agreementFees?.maxLatePenaltyDays} ({numberToWordSmall(agreementFees?.maxLatePenaltyDays || 0)}) days of the refund payout date, the Service Provider is irrevocably authorized to activate the debit order immediately.</p>
              <p className="ml-4"><em>Debit Amount:</em> In such an event, the debit order will be processed for the total of the outstanding Refund Facilitation Fee (and all other outstanding fees) PLUS a fixed penalty of {formatCurrencyFull(calculateMaxLatePenalty())} (calculated as {agreementFees?.maxLatePenaltyDays} days x {formatCurrencyFull(agreementFees?.dailyLatePenalty)} late payment penalty per day).</p>

              {/* Section 3 */}
              <h3 className="font-bold text-lg underline">3. OBLIGATIONS OF THE CLIENT</h3>
              
              <p><span className="font-semibold">3.1</span> The Client undertakes to provide all necessary documentation (IRP5s, medical aid certificates, logbooks, bank statements, etc.) timeously upon request by the Service Provider.</p>
              
              <p><span className="font-semibold">3.2</span> The Client acknowledges that the Service Provider cannot act without these documents.</p>

              {/* Section 4 */}
              <h3 className="font-bold text-lg underline">4. TERMINATION AND "GHOSTING" CLAUSE</h3>
              
              <p><span className="font-semibold">4.1</span> <strong>Voluntary Termination:</strong> Either party may terminate this agreement via written notice.</p>
              
              <p><span className="font-semibold">4.2</span> <strong>Deemed (Constructive) Termination:</strong> The Client is deemed to have terminated this agreement if:</p>
              <ul className="list-disc list-inside ml-8 space-y-1">
                <li>The Client removes the Service Provider's access to his/her tax profile from the SARS eFiling Tax Practitioner Portal; OR</li>
                <li>The Client fails to respond to requests for information or documentation for a period exceeding 14 (fourteen) days ("Ghosting"); OR</li>
                <li>The Client fails to pay the Initiation Fee or any invoice within 7 (seven) days of presentation.</li>
                <li>The Client fails to pay the Submission Fee or any invoice within 7 (seven) days of presentation.</li>
              </ul>

              <p><span className="font-semibold">4.3</span> <strong>Termination Fee:</strong> In the event of termination (whether Voluntary or Deemed), a Termination Fee of {formatCurrencyFull(agreementFees?.terminationFee)} shall immediately become due and payable to cover work done to date, administrative costs, and reservation of capacity. This will be collected by debit order if payment is not received within 7 (seven) days of termination date.</p>

              {/* Section 5 - formerly Section 6 */}
              <h3 className="font-bold text-lg underline">5. LIMITATION OF LIABILITY AND INDEMNITY</h3>
              
              <p><span className="font-semibold">5.1</span> While the Service Provider acts with due professional care, the Service Provider does not guarantee any specific tax refund from SARS.</p>
              
              <p><span className="font-semibold">5.2</span> The Service Provider is not liable for penalties or interest levied by SARS resulting from the Client's failure to provide accurate information or funds timeously.</p>

              {/* Section 6 - formerly Section 5 */}
              <h3 className="font-bold text-lg underline">6. DEBT COLLECTION, CREDIT RATING AND TPN</h3>
              
              <p><span className="font-semibold">6.1</span> The Client acknowledges their liability for services rendered and agrees to honor all valid invoices.</p>
              
              <p><span className="font-semibold">6.2</span> <strong>Debt Collection:</strong> Should the Client fail to settle any invoice, the Service Provider reserves the right to hand the account over to a debt collection agency or legal practitioner.</p>
              
              <p><span className="font-semibold">6.3</span> <strong>TPN (Pty) Ltd:</strong> The Client specifically consents to the Service Provider utilizing TPN (Pty) Ltd for debt collection purposes.</p>
              
              <p><span className="font-semibold">6.4</span> <strong>Credit Bureau Listing:</strong> The Client acknowledges and consents that in the event of non-payment, the Service Provider may submit a purely factual report regarding the Client's default to TPN (Pty) Ltd and other registered Credit Bureaus. This will negatively impact the Client's credit profile and credit score.</p>
              
              <p><span className="font-semibold">6.5</span> <strong>Costs:</strong> The Client shall be liable for all legal costs incurred by the Service Provider in enforcing this agreement on the attorney-and-client scale, including collection commission and tracing fees.</p>

              {/* Section 7 */}
              <h3 className="font-bold text-lg underline">7. GENERAL</h3>
              
              <p><span className="font-semibold">7.1</span> <strong>Entire Agreement:</strong> This document, together with Annexure A and Annexure B, constitutes the sole record of the agreement between the parties. No variation or suspension of any term shall be valid unless in writing and signed by both parties.</p>
              
              <p><span className="font-semibold">7.2</span> <strong>Governing Law:</strong> This agreement is governed by the laws of the Republic of South Africa.</p>
              
              <p><span className="font-semibold">7.3</span> <strong>Jurisdiction:</strong> The parties consent to the jurisdiction of the Magistrates' Court for any litigation arising from this agreement, notwithstanding that the amount in dispute may exceed the jurisdiction of such court.</p>

              {/* Section 8 - Acceptance */}
              <h3 className="font-bold text-lg underline">8. ACCEPTANCE</h3>
              
              <h3 className="font-bold text-lg underline">Accepted by client by virtue of signature</h3>
            </div>

            {/* Client Signature Section */}
            <div className="space-y-4 text-sm sm:text-base text-gray-800">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Client name:</span>
                <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{fullName}</span>
              </div>

              {/* Client Signature */}
              <div>
                <p className="font-semibold mb-3">Client signature:</p>
                <SignatureCanvas 
                  ref={signatureCanvasRef}
                  onSignatureChange={handleSignatureChange}
                  onClear={handleClearSignature}
                  heightClass="h-40 sm:h-48"
                  initialSignature={signature}
                />
              </div>

              <br />

              {/* Signed at Section */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>
                  Signed at <input 
                    type="text" 
                    value={signedAt}
                    onChange={handleLocationChange}
                    onBlur={(e) => setSignedAt(e.target.value.trim())}
                    placeholder="Enter location"
                    className="border-b-2 border-tca-blue bg-blue-50 px-2 py-1 w-40 focus:outline-none font-bold text-tca-blue rounded"
                  /> on this <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{dayOfMonth}</span> day of <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{monthName} {fullYear}</span>
                </p>
                {locationError && <p className="text-sm text-red-600 mt-2">{locationError}</p>}
              </div>
            </div>

            <br />
            <br />
            <br />

            {/* Company Section */}
            <div className="border-t pt-6">
              <h3 className="font-bold text-lg underline mb-4">Accepted by Company by virtue of Director signature + Company Stamp</h3>
              <div className="space-y-2">
                <p><span className="font-semibold">Company name:</span> <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">TC Auditors (Pty) Ltd</span></p>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold whitespace-nowrap">Company stamp:</span>
                    <img 
                      src={STAMP_URL} 
                      alt="TC Auditors Company Stamp" 
                      className="w-[222px] h-auto object-contain"
                      data-pdf-only="true"
                      style={{ display: 'none' }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold whitespace-nowrap">Director signature:</span>
                    <img 
                      src="/images/Director_Signature.png" 
                      alt="Director Signature" 
                      className="h-24 w-auto object-contain"
                      data-pdf-only="true"
                      style={{ display: 'none' }}
                    />
                  </div>
                </div>
              </div>
              
              <br />
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p>
                  Signed at <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">Meyerton</span> on this <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{dayOfMonth}</span> day of <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{monthName} {fullYear}</span>
                </p>
              </div>
            </div>

            <br />
            <br />
            <br />
            <br />

            {/* Action Buttons - Hidden in PDF */}
            <div className="flex flex-col sm:flex-row gap-3 items-center mt-8 pt-6 border-t" data-hide-in-pdf="true">
              <a 
                href="https://tax.tcauditors.co.za"
                className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
                data-hide-in-pdf="true"
              >
                Start Over
              </a>
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
                className="sm:w-auto"
              >
                ← Back to Page 3
              </Button>
              
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid() || isGeneratingPDF}
                className="sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? 'Saving...' : 'Go to Next Page...'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={4} totalPages={7} pageTitle="Tax Services Master Engagement Letter" />
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>

      {/* Location Discrepancy Warning Modal */}
      <AlertDialog open={showLocationWarning} onOpenChange={setShowLocationWarning}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Location Discrepancy
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-sm">
                <p>
                  The location you entered on this page does not match the location on the previous page. 
                  Please ensure consistency across all pages.
                </p>
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">POPI Consent Agreement:</span>
                    <span className="font-bold text-tca-blue">{clientData.signedAtPage3 || 'Not entered'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Tax Services Master Engagement Letter:</span>
                    <span className="font-bold text-tca-blue">{signedAt}</span>
                  </div>
                </div>
                <p className="text-gray-600">
                  Please go back and correct the location on either the <strong>POPI Consent Agreement</strong> or the <strong>Tax Services Master Engagement Letter</strong> to be consistent.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowLocationWarning(false)}>
              OK, I'll correct it
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PageFour;
